import { BehaviorSubject } from 'rxjs';
import { History } from 'history';

/**
 * @description observable of popup components
 *	a component can subscribe to this and render the components that are added
 *	this allows a child component to add components to other locations in the tree
 */
export const popup = new BehaviorSubject(null);

/**
 * @description closes the currently open popup
 * @param history
 */
export const closePopup = (history: History): (() => void) => {
  return (): void => {
    history.replace(history.location.pathname);
  };
};

/**
 * @description opens a popup
 * @param component
 * @param history
 * @param key
 */
export const openPopup = (
  component: React.ReactElement,
  history: History,
  key: string
): void => {
  history.push(`${history.location.pathname}#${key}`);
  popup.next(component);
};

/**
 * @description observable of inner popup components
 *	a component can subscribe to this and render the components that are added
 *	this allows a popup to render another popup inside of it
 */
export const innerPopup = new BehaviorSubject(null);

/**
 * @description closes the inner popup
 * @param history
 */
export const closeInnerPopup = (history: History): (() => void) => {
  return (): void => {
    /// remove inner popup segment
    const hashSegs = history.location.hash.split('/');
    history.replace(`${history.location.pathname}${hashSegs[0]}`);
  };
};

/**
 * @description opens the inner popup
 * @param component
 * @param history
 * @param key
 */
export const openInnerPopup = (
  component: React.ReactElement,
  history: History,
  key: string
): void => {
  /// remove inner popup segment, if its exists
  const hashSegs = history.location.hash.split('/');
  history.push(`${history.location.pathname}${hashSegs[0]}/${key}`);
  innerPopup.next(component);
};

/**
 * @description observable of top level popup components
 *	a component can subscribe to this and render the components that are added
 *	this allows a popup to render another popup inside of it
 */
export const topLevelPopup = new BehaviorSubject(null);

/**
 * @description closes the top level popup
 */
export const closeTopLevelPopup = (): void => topLevelPopup.next(null);

/**
 * @description opens the top level popup
 * @param component
 */
export const openTopLevelPopup = (component: React.ReactElement): void => {
  topLevelPopup.next(component);
};
